import type { ActivationLifecycle } from '@/infra/activation'
import type { GetCurrentWorkspaceFn } from '@/features/iam'
import { action, computed, observable, runInAction } from 'mobx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { Checklist } from '@/features/jobs/details/tax-type/info-gathering/Checklist'
import type { MeasureOneStore } from '@/features/jobs/details/tax-type/info-gathering/measure-one/MeasureOneStore'
import { Id } from '@/infra/id'
import {
  MEASURE_ONE_SUPPORTED_DOCUMENT_TYPES,
  type MeasureOneExperience,
  type WellKnownDocumentTypeShortId,
} from '@/features/jobs/details/tax-type/info-gathering/measure-one/mappers'
import { task } from 'mobx-task'
import { DocumentSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/documents/DocumentSupportingItemInput'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'

export class MeasureOneViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Modal for starting the measure one import documents flow
   */
  readonly measureOneImportModalVm = new ModalViewModel()

  /**
   * Flag to know when to keep showing the import modal.
   */
  @observable accessor importModalDismissed = false

  /**
   * fetched access token for measure one.
   */
  @observable accessor accessToken: string | null = null

  /**
   * fetched data request id for measure one.
   */
  @observable accessor dataRequestId: string | null = null

  constructor(
    private submittedJob: SubmittedJob,
    private getCheckList: () => Checklist | null,
    private readonly _getCurrentWorkspace: GetCurrentWorkspaceFn,
    private readonly measureOneStore: MeasureOneStore,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
  ) {}

  /**
   * Weather m1 is enabled for the given workspace
   */
  @computed
  get isEnabled() {
    return this._getCurrentWorkspace().config.measureOne.enabled
  }

  /**
   * All requested documents
   */
  @computed
  get requestedDocumentItems() {
    return this.getCheckList()?.allItems ?? []
  }

  /**
   * M1 documents that are compatible with our requested documents
   */
  @computed
  get measureOneSupportedDataTypes() {
    // E2E doc types short ID must have a suffix on E2E tests
    // this will map the id generated for the e2e and map it to a valid doc type
    if (this.submittedJob.workspace.name.includes('E2E-')) {
      const wsName = this.submittedJob.workspace.name

      return this.requestedDocumentItems.map(({ input }) => {
        const value = input as DocumentSupportingItemInput
        return Id.assert(value.documentTypeShortId).replace(
          `_${wsName}`,
          '',
        ) as WellKnownDocumentTypeShortId
      })
    }

    return this.requestedDocumentItems
      .map((x) => x.input)
      .filter(
        (input): input is DocumentSupportingItemInput =>
          input instanceof DocumentSupportingItemInput &&
          MEASURE_ONE_SUPPORTED_DOCUMENT_TYPES.has(
            Id.assert(input.documentTypeShortId),
          ),
      )
      .map((input) => input.documentTypeShortId as WellKnownDocumentTypeShortId)
  }

  /**
   * Weather we can show the import modal when first reaching info gathering
   */
  @computed
  get canShowImportModal() {
    return (
      this.isEnabled &&
      !this.importModalDismissed &&
      this.measureOneSupportedDataTypes.length > 0
    )
  }

  activate() {
    this._lifecycle.activate()

    if (this.canShowImportModal) {
      this.measureOneImportModalVm.show()
    }
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Dismisses the connection modal and makes sure we don't show it again
   */
  @action.bound
  dismissModal() {
    this.measureOneImportModalVm.close()

    this.importModalDismissed = true
  }

  /**
   * Fetches the data we need to initialize the measure one import flow
   */
  initializeMeasureOneImportFlow = task.resolved(
    async (experience: MeasureOneExperience) => {
      try {
        const dataRequestId =
          await this.measureOneStore.initializeJobTaxDataImport(
            Id.assert(this.submittedJob.id),
            Id.assert(this.submittedJob.workspace.id),
            experience,
            this.measureOneSupportedDataTypes,
          )

        const accessToken =
          await this.measureOneStore.generatePublicAccessToken(
            Id.assert(this.submittedJob.id),
          )

        runInAction(() => {
          this.accessToken = accessToken
          this.dataRequestId = dataRequestId
        })
      } catch (e) {
        this._createSnackbarErrorHandler('Unable to initialize measure one')(e)
      }
    },
  )
}
